<template>
    <v-card outlined elevation="1" class="mt-4">
        <v-card-text class="pb-2">
            <v-row class="align-center">
                <v-col cols="12" md="4">
                    <h2 class="section-subtitle mb-0">Ventas</h2>
                </v-col>

                <v-col cols="12" md="4">
                    <v-text-field
                    v-model="search"
                    label="Buscar por Folio / Cliente / Vendedor..."
                    append-icon="mdi-magnify"
                    dense
                    outlined
                    hide-details
                    clearable
                    />
                </v-col>

                <v-col cols="12" sm="6" md="2">
                    <v-select
                    v-model="paymentFilter"
                    :items="paymentOptions"
                    label="Forma de pago"
                    dense
                    outlined
                    hide-details
                    clearable
                    />
                </v-col>

                <v-col cols="12" sm="6" md="2">
                    <v-select
                    v-model="invoiceFilter"
                    :items="invoiceOptions"
                    label="Facturación"
                    dense
                    outlined
                    hide-details
                    clearable
                    />
                </v-col>
            </v-row>
        </v-card-text>

        <v-divider />

        <v-card-text class="py-4">
            <v-row>
                <v-col cols="12" class="d-flex flex-nowrap">
                    <money-total-card
                    icon="mdi-cash"
                    headerText="Total Efectivo"
                    :total="totals.cash"
                    class="flex-grow-1 mr-2"
                    />
                    <money-total-card
                    icon="mdi-credit-card"
                    headerText="T. Débito"
                    :total="totals.debit"
                    class="flex-grow-1 mr-2"
                    />
                    <money-total-card
                    icon="mdi-credit-card"
                    headerText="T. Crédito"
                    :total="totals.credit"
                    class="flex-grow-1 mr-2"
                    />
                    <money-total-card
                    icon="mdi-checkbook"
                    headerText="Cheques"
                    :total="totals.check"
                    class="flex-grow-1 mr-2"
                    />
                    <money-total-card
                    icon="mdi-transfer-right"
                    headerText="Transferencias"
                    :total="totals.transfer"
                    class="flex-grow-1 mr-2"
                    />
                </v-col>
            </v-row>
        </v-card-text>

        <v-divider />

        <v-data-table
        :headers="headers"
        :items="filteredOrders"
        :items-per-page="5"
        :loading="loading"
        class="elevation-0"
        >
          <template #[`item.paymentMethod`]="{ item }">
            <div class="d-flex align-center">
              <v-icon small class="mr-2">{{ item.payment.icon }}</v-icon>
              <template v-if="item.payment.label === 'Varios'">
                <v-tooltip bottom max-width="360">
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on" class="d-flex align-center">
                      <span class="text-subtitle-1">Varios</span>
                    </span>
                  </template>
                  <div>
                    <div class="font-weight-medium mb-1">Desglose</div>
                    <div
                      v-for="(pm, idx) in item.payment.items"
                      :key="idx"
                      class="d-flex justify-space-between"
                    >
                      <span>{{ pm.label }}</span>
                      <span class="ml-4">{{ pm.amountStr }}</span>
                    </div>
                  </div>
                </v-tooltip>
              </template>
              <template v-else>
                <span class="text-subtitle-1">{{ item.payment.label }}</span>
              </template>
            </div>
          </template>

          <template #[`item.invoiceStatus`]="{ item }">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  x-small
                  width="100"
                  dark
                  :color="getInvoiceStatusData(item).color"
                  v-bind="attrs"
                  v-on="on"
                  @click="handleInvoiceStatusClick(item)"
                >
                  {{ getInvoiceStatusData(item).label }}
                </v-btn>
              </template>
              {{ getInvoiceTooltipText(item) }}
            </v-tooltip>
          </template>

          <template #[`item.totalAmountStr`]="{ item }">
              <span class="font-weight-medium">{{ item.totalAmountStr }}</span>
          </template>

          <template #[`item.actions`]="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                  @click="$emit('view-order', item.raw)"
                >
                  <v-icon>mdi-eye</v-icon>
                </v-btn>
              </template>
              <span>Ver detalle</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="error"
                  v-bind="attrs"
                  v-on="on"
                  @click="openDelete(item)"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>Eliminar venta</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="green"
                  v-bind="attrs"
                  v-on="on"
                  @click="reprintTicket(item)"
                >
                  <v-icon>mdi-printer</v-icon>
                </v-btn>
              </template>
              <span>Reimprimir ticket</span>
            </v-tooltip>
          </template>
        </v-data-table>

        <v-dialog v-model="deleteModal" width="520" persistent>
          <v-card>
            <v-card-title class="text-center justify-center text-h5 grey lighten-2">
              Eliminar orden de compra #{{ selected && selected.id ? selected.id : '' }}
            </v-card-title>
            <v-card-text v-if="!validUser">
              <v-container>
                <v-row>
                  <v-col class="text-center grey--text text-h6" cols="12">
                    Ingrese credenciales de administrador para continuar
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      append-icon="mdi-account"
                      label="Usuario"
                      type="text"
                      v-model="username"
                      required
                      hide-details
                    />
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      :type="hidePassword ? 'password' : 'text'"
                      :append-icon="hidePassword ? 'mdi-eye-off' : 'mdi-eye'"
                      label="Contraseña"
                      @click:append="hidePassword = !hidePassword"
                      v-model="password"
                      @keypress.enter="authUser"
                      required
                      hide-details
                    />
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
            <v-card-text v-else>
              <v-container v-if="block === 0" class="text-center red--text text-h6">
                ¿Está seguro que desea eliminar esta orden de compra?
              </v-container>
              <v-container v-if="block === 1" class="text-center red--text text-h6">
                Espere un momento mientras se procesa su petición.
              </v-container>
            </v-card-text>
            <v-divider />
            <v-card-actions v-if="!validUser">
              <v-spacer />
              <v-btn color="error" text @click="closeDelete">Cancelar</v-btn>
              <v-btn color="success" text @click="authUser">Continuar</v-btn>
            </v-card-actions>
            <v-card-actions v-else>
              <v-spacer />
              <v-btn v-if="block === 0" color="error" text @click="closeDelete">Cancelar</v-btn>
              <v-btn v-if="block === 0"
              color="success" text @click="confirmDelete">Confirmar</v-btn>
              <v-progress-linear
                indeterminate
                color="green"
                v-if="block === 1"
              />
            </v-card-actions>
          </v-card>
        </v-dialog>
    </v-card>
</template>

<script>
import { mapGetters } from 'vuex';
import { purchaseOrderToTicketData } from '@/serializers/PurchaseOrder.serializer';
import Swal from 'sweetalert2';
import MoneyTotalCard from './MoneyTotalCard.vue';
import AuthService from '../../services/AuthService';
import PurchaseOrderService from '../../services/PurchaseOrderService';

export default {
  name: 'purchase-order-table',
  components: { MoneyTotalCard },
  props: {
    orders: { type: Array, default: () => [] },
    totals: {
      type: Object,
      default: () => ({
        cash: '$0.00',
        debit: '$0.00',
        credit: '$0.00',
        check: '$0.00',
        transfer: '$0.00',
      }),
    },
    loading: { type: Boolean, default: false },
    invoiceSummary: {
      type: Object,
      default: () => ({ pending: 0, particular: 0, global: 0 }),
    },
  },
  data: () => ({
    search: '',
    paymentFilter: null,
    invoiceFilter: null,
    paymentOptions: ['Efectivo', 'Débito', 'Crédito', 'Cheque', 'Transferencia', 'Varios'],
    invoiceOptions: ['Pendiente', 'Particular', 'Global'],
    filtersTimer: null,
    headers: [
      { text: 'Folio', value: 'orderNumber' },
      { text: 'Fecha', value: 'createdDtStr' },
      { text: 'Cliente', value: 'clientName' },
      { text: 'Vendedor', value: 'employeeName' },
      { text: 'Forma de pago', value: 'paymentMethod', sortable: false },
      { text: 'Facturación', value: 'invoiceStatus', sortable: false },
      { text: 'Totales', value: 'totalAmountStr', align: 'end' },
      { text: 'Acciones', value: 'actions', sortable: false },
    ],
    deleteModal: false,
    selected: null,
    username: '',
    password: '',
    hidePassword: true,
    validUser: false,
    block: 0,
  }),
  computed: {
    ...mapGetters({
      ticketStruct: 'ticketData',
      stompClient: 'stompClient',
    }),

    safeOrders() {
      return Array.isArray(this.orders) ? this.orders : [];
    },

    mappedOrders() {
      return this.safeOrders.map((order) => {
        const n = this.normalizeOrder(order);

        const payment = this.buildPaymentSummary(n);
        const invoiceStatus = this.getInvoiceStatus(n);

        return {
          id: n.id,
          orderNumber: n.foil || n.id,
          createdDtStr: n.createdDtStr,
          clientName: n.clientName,
          employeeName: n.employeeName,
          totalAmountStr: n.totalAmountStr,

          payment,
          paymentMethod: payment.label,
          invoiceStatus,
          invoiceId: n.invoiceId,
          invoiceSerie: n.invoiceSerie,

          raw: order,
        };
      });
    },
    filteredOrders() {
      const s = (this.search || '').trim().toLowerCase();
      const selectedPayment = this.paymentFilter || 'Todos';
      const selectedInvoice = this.invoiceFilter || 'Todos';
      return this.mappedOrders.filter((item) => {
        const text = `${item.orderNumber || ''} ${item.clientName || ''} ${item.employeeName || ''}`.toLowerCase();
        const searchOk = !s || text.includes(s);
        const paymentOk = selectedPayment === 'Todos'
          ? true
          : this.orderHasPayment(item, selectedPayment);
        const invoiceOk = selectedInvoice === 'Todos' || item.invoiceStatus === selectedInvoice;
        return searchOk && paymentOk && invoiceOk;
      });
    },
  },
  watch: {
    search() { this.emitFiltersDebounced(); },
    paymentFilter() { this.emitFiltersDebounced(); },
    invoiceFilter() { this.emitFiltersDebounced(); },
  },
  methods: {
    orderHasPayment(item, filterLabel) {
      const p = item?.payment;
      if (!p) return false;
      if (filterLabel === 'Varios') return p.label === 'Varios';
      if (p.label === filterLabel) return true;
      const sub = Array.isArray(p.items) ? p.items : [];
      return sub.some((x) => x && x.label === filterLabel);
    },
    emitFiltersDebounced() {
      if (this.filtersTimer) clearTimeout(this.filtersTimer);
      this.filtersTimer = setTimeout(() => {
        this.$emit('filters-change', this.buildServerFilters());
      }, 350);
    },

    buildServerFilters() {
      const q = (this.search || '').trim();
      const pf = this.paymentFilter || 'Todos';
      const inf = this.invoiceFilter || 'Todos';
      const paymentMap = {
        Todos: null,
        Efectivo: 'cash',
        Débito: 'debit',
        Crédito: 'credit',
        Cheque: 'check',
        Transferencia: 'transfer',
        Varios: 'many',
      };
      const invoiceMap = {
        Todos: null,
        Pendiente: 'pending',
        Particular: 'particular',
        Global: 'global',
      };
      return {
        q,
        paymentMethod: paymentMap[pf] ?? null,
        invoiceStatus: invoiceMap[inf] ?? null,
      };
    },
    getFirstDefined(obj, paths, fallback = '') {
      for (let i = 0; i < paths.length; i += 1) {
        const path = paths[i];

        const parts = String(path).split('.');
        let val = obj;

        for (let j = 0; j < parts.length; j += 1) {
          const key = parts[j];
          if (val && val[key] !== undefined) val = val[key];
          else { val = undefined; break; }
        }

        if (val !== undefined && val !== null && val !== '') return val;
      }
      return fallback;
    },

    moneyStr(value) {
      const num = typeof value === 'number'
        ? value
        : Number(String(value || '').replace(/[^\d.-]/g, ''));

      const safe = Number.isFinite(num) ? num : 0;

      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2,
      }).format(safe);
    },

    normalizeOrder(order) {
      const id = this.getFirstDefined(order, ['id', 'orderId', 'purchaseOrderId'], null);

      const createdDtStr = this.getFirstDefined(order, [
        'createdDtStr',
        'created_dt_str',
        'createdAtStr',
        'created_at_str',
        'createdAt',
        'created_at',
        'createdDt',
      ], '');

      const clientName = this.getFirstDefined(order, [
        'clientName',
        'client_name',
        'client.name',
        'customer.name',
        'customerName',
      ], '—');

      const employeeName = this.getFirstDefined(order, [
        'employeeName',
        'employee_name',
        'employee.name',
        'seller.name',
        'vendor.name',
        'vendorName',
      ], '—');

      const invoiceId = this.getFirstDefined(order, [
        'invoiceId',
        'invoice.id',
        'invoice_id',
      ], null);
      const invoiceSerie = this.getFirstDefined(order, [
        'invoice.serie',
        'invoiceSerie',
        'serie',
      ], '');
      const foil = this.getFirstDefined(order, ['foil', 'folio', 'orderNumber'], null);

      const totalAmountStr = this.getFirstDefined(order, ['totalAmountStr'], null)
      || this.moneyStr(this.getFirstDefined(order, ['totalAmount', 'total_amount', 'total'], 0));

      const cashAmountStr = this.getFirstDefined(order, ['cashAmountStr'], null)
        || this.moneyStr(this.getFirstDefined(order, ['cashAmount', 'cash_amount'], 0));

      const debitCardAmountStr = this.getFirstDefined(order, ['debitCardAmountStr'], null)
        || this.moneyStr(this.getFirstDefined(order, ['debitCardAmount', 'debit_card_amount', 'debitAmount'], 0));

      const creditCardAmountStr = this.getFirstDefined(order, ['creditCardAmountStr'], null)
        || this.moneyStr(this.getFirstDefined(order, ['creditCardAmount', 'credit_card_amount', 'creditAmount'], 0));

      const checkAmountStr = this.getFirstDefined(order, ['checkAmountStr'], null)
        || this.moneyStr(this.getFirstDefined(order, ['checkAmount', 'check_amount'], 0));

      const transferAmountStr = this.getFirstDefined(order, ['transferAmountStr'], null)
        || this.moneyStr(this.getFirstDefined(order, ['transferAmount', 'transfer_amount'], 0));

      return {
        ...order,
        id,
        foil,
        createdDtStr,
        clientName,
        employeeName,
        totalAmountStr,
        cashAmountStr,
        debitCardAmountStr,
        creditCardAmountStr,
        checkAmountStr,
        transferAmountStr,
        invoiceId,
        invoiceSerie,
      };
    },

    isNonZeroMoney(valueStrOrNum) {
      const n = typeof valueStrOrNum === 'number'
        ? valueStrOrNum
        : Number(String(valueStrOrNum || '').replace(/[^\d.-]/g, ''));

      return Number.isFinite(n) && n > 0;
    },

    buildPaymentSummary(order) {
      const items = [];
      if (this.isNonZeroMoney(order.cashAmountStr)) items.push({ label: 'Efectivo', amountStr: order.cashAmountStr });
      if (this.isNonZeroMoney(order.debitCardAmountStr)) items.push({ label: 'Débito', amountStr: order.debitCardAmountStr });
      if (this.isNonZeroMoney(order.creditCardAmountStr)) items.push({ label: 'Crédito', amountStr: order.creditCardAmountStr });
      if (this.isNonZeroMoney(order.checkAmountStr)) items.push({ label: 'Cheque', amountStr: order.checkAmountStr });
      if (this.isNonZeroMoney(order.transferAmountStr)) items.push({ label: 'Transferencia', amountStr: order.transferAmountStr });
      if (items.length === 0) {
        return { label: 'N/A', icon: this.paymentIcon('N/A'), items: [] };
      }
      if (items.length === 1) {
        return { label: items[0].label, icon: this.paymentIcon(items[0].label), items };
      }
      return { label: 'Varios', icon: this.paymentIcon('Varios'), items };
    },

    getInvoiceStatus(order) {
      if (['Pendiente', 'Particular', 'Global'].includes(order.invoiceStatus)) return order.invoiceStatus;
      if (!order.invoiceId) return 'Pendiente';
      const serie = String(order.invoiceSerie || '').toUpperCase();
      if (serie.includes('G')) return 'Global';
      return 'Particular';
    },

    invoiceColor(status) {
      if (status === 'Particular') return 'success';
      if (status === 'Global') return 'primary';
      return 'grey';
    },

    paymentIcon(label) {
      const map = {
        Efectivo: 'mdi-cash',
        Débito: 'mdi-credit-card-outline',
        Crédito: 'mdi-credit-card',
        Cheque: 'mdi-checkbook',
        Transferencia: 'mdi-bank-transfer',
        Varios: 'mdi-layers-triple',
        'N/A': 'mdi-minus-circle-outline',
      };
      return map[label] || 'mdi-cash';
    },

    openDelete(item) {
      this.selected = item;
      this.deleteModal = true;
      this.validUser = false;
      this.block = 0;
      this.username = '';
      this.password = '';
      this.hidePassword = true;
    },

    closeDelete() {
      this.deleteModal = false;
      this.selected = null;
      this.validUser = false;
      this.block = 0;
    },

    async reprintTicket(item) {
      Swal.fire({
        icon: 'info',
        title: 'Imprimiendo ticket',
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true,
      });
      const data = await PurchaseOrderService.getPurchaseOrderDataFromFolio(item.raw.foil);
      if (this.stompClient && this.stompClient.connected) {
        const ticketData = purchaseOrderToTicketData(data, this.ticketStruct);
        const payload = JSON.stringify(ticketData);

        // eslint-disable-next-line max-len
        this.stompClient.send('/exchange/orders/orders', payload, { 'content-type': 'application/json' });
      }
    },

    async authUser() {
      this.validUser = false;
      if (!this.username) {
        Swal.fire('Error', 'Ingresa el usuario', 'error');
        return;
      }
      if (!this.password) {
        Swal.fire('Error', 'Ingresa la contraseña', 'error');
        return;
      }
      const resp = await AuthService.authAdmin(this.username, this.password);
      if (resp?.error) {
        Swal.fire('Error', 'No se puede autenticar, favor de contactar con un administrador.', 'error');
        return;
      }
      this.validUser = true;
      this.username = '';
      this.password = '';
    },

    async confirmDelete() {
      if (!this.selected || !this.selected.id) return;
      this.block = 1;
      const { id } = this.selected;
      const resp = await PurchaseOrderService.deletePurchaseOrder(id);
      if (resp?.error) {
        Swal.fire('Error', `${resp.error}`, 'error');
        this.block = 0;
        this.closeDelete();
        return;
      }
      Swal.fire('Listo', `La orden de compra #${id} se ha eliminado exitosamente.`, 'success');
      this.block = 0;
      this.closeDelete();
      this.$emit('delete-order', id);
    },
    getInvoiceId(item) {
      return item?.invoiceId || null;
    },
    isGlobalInvoice(item) {
      const serie = String(item?.invoiceSerie || '').toUpperCase();
      return serie.includes('G');
    },
    getInvoiceStatusData(item) {
      const status = item?.invoiceStatus || 'Pendiente';
      if (status === 'Particular') return { label: 'Particular', color: 'success' };
      if (status === 'Global') return { label: 'Global', color: 'primary' };
      return { label: 'Pendiente', color: 'blue-grey' };
    },
    canDownloadInvoice(item) {
      return this.getInvoiceStatusData(item).label === 'Particular';
    },
    getInvoiceTooltipText(item) {
      const status = this.getInvoiceStatusData(item).label;
      if (status === 'Particular') return 'Descargar XML y PDF';
      if (status === 'Global') return 'Factura global: no disponible para descarga aquí';
      return 'Pendiente de facturar';
    },
    handleInvoiceStatusClick(item) {
      if (!this.canDownloadInvoice(item)) return;
      this.invoicePdf(item);
      this.invoiceXml(item);
    },
    invoicePdf(item) {
      const invoiceId = this.getInvoiceId(item);
      if (!invoiceId) return;
      window.open(`${process.env.VUE_APP_BASEURL}/invoice/${invoiceId}/pdf`, '_blank');
    },
    invoiceXml(item) {
      const invoiceId = this.getInvoiceId(item);
      if (!invoiceId) return;
      window.open(`${process.env.VUE_APP_BASEURL}/invoice/${invoiceId}/xml`, '_blank');
    },
  },
};
</script>

<style scoped>
.section-subtitle {
    font-weight: 400;
    font-size: 1.8rem;
}
</style>
